import styled from "styled-components";
import { useEffect, useMemo, useRef, useState } from "react";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";

const Title = styled.h2`
    margin: 0 0 15px 0;
    font-size: 18px;
    color: var(--ctrl-text, #333);
`;

const FieldGroup = styled.div`
    margin-bottom: 12px;
`;

const InputLabel = styled.label`
    display: block;
    margin-bottom: 5px;
    font-size: 14px;
    color: var(--ctrl-text-muted, #555);
`;

const Input = styled.input`
    width: 100%;
    padding: 8px;
    box-sizing: border-box;
    border: 1px solid var(--ctrl-border, #ccc);
    border-radius: 4px;
    font-size: 14px;

    &:focus {
        border-color: #007bff;
        outline: none;
        box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
    }
`;

const CheckboxWrapper = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    margin-bottom: 12px;
    font-size: 14px;
    color: var(--ctrl-text-muted, #555);
    cursor: pointer;
`;

const BotoesWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
`;

const BotaoSecundario = styled.button`
    padding: 8px 16px;
    background-color: transparent;
    color: var(--ctrl-text, #333);
    border: 1px solid var(--ctrl-border, #ccc);
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        background-color: var(--ctrl-surface-muted, #f0f0f0);
    }
`;

const BotaoAplicar = styled.button`
    padding: 8px 16px;
    background-color: #007bff;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        background-color: #0056b3;
    }
`;

const MultiWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const MultiControl = styled.button`
    width: 100%;
    min-height: 38px;
    padding: 8px;
    box-sizing: border-box;
    border: 1px solid ${({ open }) => (open ? "#007bff" : "var(--ctrl-border, #ccc)")};
    border-radius: 4px;
    font-size: 14px;
    background-color: var(--ctrl-surface, #fff);
    color: ${({ empty }) => (empty ? "#999" : "var(--ctrl-text, #333)")};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    text-align: left;
    box-shadow: ${({ open }) => (open ? "0 0 5px rgba(0, 123, 255, 0.5)" : "none")};
`;

const MultiText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Caret = styled.span`
    font-size: 10px;
    color: #777;
    flex-shrink: 0;
`;

const MultiPanel = styled.div`
    position: absolute;
    z-index: 10;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    max-height: 240px;
    display: flex;
    flex-direction: column;
    background-color: var(--ctrl-surface, #fff);
    border: 1px solid var(--ctrl-border, #ccc);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    overflow: hidden;
`;

const MultiSearch = styled.input`
    margin: 8px;
    padding: 6px 8px;
    border: 1px solid var(--ctrl-border, #ccc);
    border-radius: 4px;
    font-size: 13px;
    &:focus {
        border-color: #007bff;
        outline: none;
    }
`;

const MultiList = styled.div`
    overflow-y: auto;
    padding: 0 4px 6px;
`;

const MultiOption = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    font-size: 13px;
    color: #444;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: #f0f4fa;
    }
`;

const MultiEmpty = styled.p`
    margin: 0;
    padding: 10px 12px;
    font-size: 13px;
    color: #999;
`;

const MultiActions = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 6px 10px;
    border-top: 1px solid #eee;
`;

const LinkButton = styled.button`
    border: none;
    background: transparent;
    color: #007bff;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    &:disabled {
        color: #bbb;
        cursor: default;
    }
`;

const MultiSelect = ({ options = [], value = [], onChange, placeholder = "Todos" }) => {
    const [open, setOpen] = useState(false);
    const [busca, setBusca] = useState("");
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickFora = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickFora);
        return () => document.removeEventListener("mousedown", handleClickFora);
    }, [open]);

    const opcoesFiltradas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!termo) return options;
        return options.filter(
            (o) =>
                String(o.nome).toLowerCase().includes(termo) ||
                String(o.cod).toLowerCase().includes(termo)
        );
    }, [options, busca]);

    const toggleValor = (cod) => {
        if (value.includes(cod)) {
            onChange(value.filter((v) => v !== cod));
        } else {
            onChange([...value, cod]);
        }
    };

    const textoResumo = useMemo(() => {
        if (value.length === 0) return placeholder;
        if (value.length === 1) {
            const sel = options.find((o) => o.cod === value[0]);
            return sel ? sel.nome : value[0];
        }
        return `${value.length} selecionados`;
    }, [value, options, placeholder]);

    return (
        <MultiWrapper ref={wrapperRef}>
            <MultiControl
                type="button"
                open={open}
                empty={value.length === 0}
                onClick={() => setOpen((v) => !v)}
            >
                <MultiText>{textoResumo}</MultiText>
                <Caret>{open ? "▲" : "▼"}</Caret>
            </MultiControl>
            {open && (
                <MultiPanel>
                    <MultiSearch
                        type="text"
                        placeholder="Buscar..."
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        autoFocus
                    />
                    <MultiList>
                        {opcoesFiltradas.length === 0 ? (
                            <MultiEmpty>Nenhuma opção encontrada.</MultiEmpty>
                        ) : (
                            opcoesFiltradas.map((o) => (
                                <MultiOption key={o.cod}>
                                    <input
                                        type="checkbox"
                                        checked={value.includes(o.cod)}
                                        onChange={() => toggleValor(o.cod)}
                                    />
                                    {o.nome}
                                </MultiOption>
                            ))
                        )}
                    </MultiList>
                    <MultiActions>
                        <LinkButton
                            type="button"
                            onClick={() => onChange(options.map((o) => o.cod))}
                            disabled={options.length === 0}
                        >
                            Selecionar todos
                        </LinkButton>
                        <LinkButton
                            type="button"
                            onClick={() => onChange([])}
                            disabled={value.length === 0}
                        >
                            Limpar
                        </LinkButton>
                    </MultiActions>
                </MultiPanel>
            )}
        </MultiWrapper>
    );
};

/**
 * Modal de filtros configurável para Controladoria.
 *
 * fields: Array<{
 *   name: string,
 *   label: string,
 *   type?: 'text' | 'date' | 'checkbox' | 'multiselect',
 *   placeholder?: string,
 *   options?: Array<{ cod, nome }>, // multiselect
 *   id?: string,
 * }>
 */
const ModalFiltersControladoria = ({
    fecharModal,
    filtros,
    setFiltros,
    aplicarFiltros,
    removerFiltros,
    fields = [],
    title = "Filtros",
    applyLabel = "Aplicar Filtros",
    clearLabel = "Remover Filtros",
}) => {
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFiltros((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const setLista = (name) => (novaLista) => {
        setFiltros((prev) => ({ ...prev, [name]: novaLista }));
    };

    return (
        <Overlay state={true}>
            <Modal size="md" header close={fecharModal}>
                <Title>{title}</Title>
                {fields.map((field) => {
                    const type = field.type || "text";
                    const fieldId = field.id || `filtro-${field.name}`;

                    if (type === "checkbox") {
                        return (
                            <CheckboxWrapper key={field.name} htmlFor={fieldId}>
                                <input
                                    type="checkbox"
                                    id={fieldId}
                                    name={field.name}
                                    checked={!!filtros[field.name]}
                                    onChange={handleChange}
                                />
                                {field.label}
                            </CheckboxWrapper>
                        );
                    }

                    if (type === "multiselect") {
                        return (
                            <FieldGroup key={field.name}>
                                <InputLabel>{field.label}</InputLabel>
                                <MultiSelect
                                    options={field.options || []}
                                    value={filtros[field.name] || []}
                                    onChange={setLista(field.name)}
                                    placeholder={field.placeholder || "Todos"}
                                />
                            </FieldGroup>
                        );
                    }

                    return (
                        <FieldGroup key={field.name}>
                            <InputLabel htmlFor={fieldId}>{field.label}</InputLabel>
                            <Input
                                type={type}
                                id={fieldId}
                                name={field.name}
                                placeholder={field.placeholder}
                                value={filtros[field.name] ?? ""}
                                onChange={handleChange}
                            />
                        </FieldGroup>
                    );
                })}
                <BotoesWrapper>
                    {typeof removerFiltros === "function" && (
                        <BotaoSecundario type="button" onClick={removerFiltros}>
                            {clearLabel}
                        </BotaoSecundario>
                    )}
                    <BotaoAplicar type="button" onClick={aplicarFiltros}>
                        {applyLabel}
                    </BotaoAplicar>
                </BotoesWrapper>
            </Modal>
        </Overlay>
    );
};

export default ModalFiltersControladoria;
